import { otpSend } from '@/actions/ecom/login';
import { getStaticContent } from '@/actions/ecom/staticContent';
import { verifyLead } from '@/actions/verifyLead';
import PageLayout from '@/components/Layouts/PageLayout';
import VerifyOTP from '@/components/VerifyOTP';
import useFormRenderer, { FormField } from '@/hooks/useFormRenderer';
import useToast from '@/hooks/useToast';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import AppBar from '@/shared/AppBar';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import GeneralSuccessScreen from '@/shared/GeneralSuccessScreen';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import { SeoData } from '@/types/seo';
import Cookies from '@/utils/cookie';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import React, { Fragment, useEffect, useState } from 'react';

interface PageProps {
  config: {
    FORM_FIELDS: FormField[];
    GRIEVANCE_SUB_ISSUES: Record<string, any>;
  };
  seo: SeoData;
}

const GrievanceForm = ({ page }: { page: PageProps }) => {
  const { FORM_FIELDS = [], GRIEVANCE_SUB_ISSUES = [] as any } = page.config;
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const fetch = (...args: any) => verifyLead.apply(null, args);
  const verifyLeadRequest = useTriggerRequest({ fetch });

  const OTP = useTriggerRequest({ fetch: (...args: any) => otpSend.apply(null, args) });
  const { triggerError } = useToast();
  const submitHandler = async (data: any) => {
    try {
      verifyLeadRequest.reset();
      form.clearErrors();
      await OTP.promisifyTrigger({ mobile_number: data.mobileNumber });
      setShowOtpScreen(true);
    } catch (error: any) {
      triggerError({ message: error?.error });
    }
  };

  const { tsx, form, updateFieldById } = useFormRenderer({
    fields: FORM_FIELDS,
    className: 'w-full flex flex-col gap-s5',
    submitHandler,
  });

  const onVerify = async (otp: string) => {
    try {
      verifyLeadRequest.reset();
      const { mobileNumber, subIssueType, issueType, ...restFormValues } = form.getValues();
      const payload = {
        leadType: 'GRIEVANCE',
        lead: {
          ...restFormValues,
          mobileNumber,
          issueType: issueType.id,
          subIssueType: subIssueType.id,
        },
        verify: {
          mobileNumber,
          otp,
          userAgent: 'WEB',
        },
        utm: {
          utm_source: Cookies.get('utm_source'),
          utm_medium: Cookies.get('utm_medium'),
          utm_channel: Cookies.get('utm_channel'),
          utm_campaign: Cookies.get('utm_campaign'),
          utm_term: Cookies.get('utm_term'),
        },
      };
      await verifyLeadRequest.promisifyTrigger(payload);
      setShowSuccess(true);
    } catch (error: any) {
      if (error?.detail?.code) {
        form.setError(error?.detail?.code, { message: error?.detail?.details });
        setShowOtpScreen(false);
        verifyLeadRequest.reset();
      }
    }
  };

  const issueType = form.watch('issueType') as any;
  const mobileNumber = form.watch('mobileNumber') as any;

  useEffect(() => {
    if (!issueType?.id) form.resetField('issueType');
    updateFieldById('subIssueType', {
      options: GRIEVANCE_SUB_ISSUES[issueType?.id] || [],
    });
    form.resetField('subIssueType');
  }, [issueType?.id]);

  useEffect(() => {
    updateFieldById('griveance_form_submit', {
      isLoading: OTP.isLoading,
    });
  }, [OTP.isLoading]);

  const renderVerifyOTP = () => {
    if (showOtpScreen && !showSuccess) {
      return (
        <Fragment>
          <AppBar onClick={() => setShowOtpScreen(false)} />
          <Container className="md:max-w-xl">
            <VerifyOTP
              title="Verify your phone number"
              subTitle={`Enter the code we just sent to <b>${mobileNumber ?? ''}</b>`}
              mobile={mobileNumber}
              onVerify={onVerify}
              verifyLeadRequest={verifyLeadRequest}
            />
          </Container>
        </Fragment>
      );
    }
    return null;
  };

  const renderSuccessScreen = () => {
    return (
      <GeneralSuccessScreen>
        <Heading className="text-center text-h-2 text-cti-primary">Thank you!</Heading>
        <Paragraph className="text-center text-body-lg text-cti-primary">
          Your complaint is important to us. We will keep you updated on its status via email. Thank
          you for your patience.
        </Paragraph>
      </GeneralSuccessScreen>
    );
  };

  const renderGrievanceForm = () => {
    if (showOtpScreen) return null;
    return (
      <Container className=" flex flex-col items-center gap-s6 md:max-w-xl mt-s6  md:mt-s9">
        <Div className="flex flex-col gap-s4">
          <Paragraph className="text-h-2  md:text-hd-2 ">Write to our Grievance Officer</Paragraph>
          <Paragraph className="text-body-lg">
            We’re sorry that we have been unable to resolve your request to your satisfaction.
            Please reach out to our Grievance Officer with your concern below.
          </Paragraph>
        </Div>
        {tsx}
      </Container>
    );
  };
  return (
    <PageLayout seo={page.seo} hideFooter mainClassName="pb-s6 md:pb-s9">
      {renderVerifyOTP()}
      {showSuccess ? renderSuccessScreen() : renderGrievanceForm()}
    </PageLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  try {
    const page = await getStaticContent('/grievance-form');
    if (!page.config?.FORM_FIELDS || !page.config?.GRIEVANCE_SUB_ISSUES) {
      throw new Error('Required form configuration is missing');
    }

    return {
      props: {
        page,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export default GrievanceForm;
